import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
    Plus, Filter, Search, ArrowUpRight, ArrowDownRight, 
    AlertTriangle, Check, X, Flag, Loader2 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import MobileLayout from '../components/layout/MobileLayout';
import RiskGauge from '../components/dashboard/RiskGauge';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../lib/utils';

const CATEGORIES = [
    'General', 'Milk', 'Grocery', 'Electronics', 'Clothing',
    'Food', 'Medicine', 'Stock', 'Rent', 'Utilities', 'Other'
];

const Transactions = () => {
    const { authAxios } = useAuth();
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedTx, setSelectedTx] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    
    // New transaction form
    const [newTx, setNewTx] = useState({
        type: 'sale',
        amount: '',
        category: 'General',
        description: ''
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const addType = searchParams.get('add');
        if (addType === 'sale' || addType === 'expense') {
            setNewTx(prev => ({ ...prev, type: addType }));
            setAddModalOpen(true);
            searchParams.delete('add');
            setSearchParams(searchParams);
        }
        
        const filter = searchParams.get('filter');
        if (filter === 'high_risk') {
            setActiveTab('high_risk');
        }
    }, [searchParams]);

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const typeParam = activeTab === 'all' ? '' : activeTab === 'high_risk' ? '' : activeTab;
            const response = await authAxios.get(`/transactions${typeParam ? `?type=${typeParam}` : ''}`);
            let data = response.data;
            
            // Filter high risk if needed
            if (activeTab === 'high_risk') {
                data = data.filter(tx => tx.risk_level === 'high');
            }
            
            setTransactions(data);
        } catch (err) {
            console.error('Fetch transactions error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [activeTab]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDateTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleString(language === 'hi' ? 'hi-IN' : 'en-IN', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleAddTransaction = async () => {
        if (!newTx.amount || parseFloat(newTx.amount) <= 0) return;
        
        setSaving(true);
        try {
            await authAxios.post('/transactions', {
                ...newTx,
                amount: parseFloat(newTx.amount)
            });
            setAddModalOpen(false);
            setNewTx({ type: 'sale', amount: '', category: 'General', description: '' });
            fetchTransactions();
        } catch (err) {
            console.error('Add transaction error:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateStatus = async (status) => {
        if (!selectedTx) return;
        
        try {
            await authAxios.put(`/transactions/${selectedTx.id}/status`, { status });
            setDetailModalOpen(false);
            fetchTransactions();
        } catch (err) {
            console.error('Update status error:', err);
        }
    };

    const filteredTransactions = transactions.filter(tx => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            tx.category?.toLowerCase().includes(query) ||
            tx.description?.toLowerCase().includes(query) ||
            tx.amount.toString().includes(query)
        );
    });

    return (
        <MobileLayout>
            <div className="space-y-4" data-testid="transactions-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold font-heading">{t('transactions')}</h1>
                    <Button 
                        onClick={() => setAddModalOpen(true)}
                        data-testid="add-transaction-btn"
                    >
                        <Plus className="w-5 h-5 mr-1" />
                        {language === 'en' ? 'Add' : 'जोड़ें'}
                    </Button>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder={language === 'en' ? 'Search transactions...' : 'लेन-देन खोजें...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="search-input"
                    />
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="all" data-testid="tab-all">
                            {language === 'en' ? 'All' : 'सभी'}
                        </TabsTrigger>
                        <TabsTrigger value="sale" data-testid="tab-sale">
                            {t('sale')}
                        </TabsTrigger>
                        <TabsTrigger value="expense" data-testid="tab-expense">
                            {t('expense')}
                        </TabsTrigger>
                        <TabsTrigger value="high_risk" data-testid="tab-risk">
                            <AlertTriangle className="w-4 h-4" />
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Transaction List */}
                {loading ? (
                    <div className="space-y-3">
                        {[1, 2, 3, 4, 5].map(i => (
                            <Skeleton key={i} className="h-20" />
                        ))}
                    </div>
                ) : filteredTransactions.length > 0 ? (
                    <div className="space-y-2">
                        {filteredTransactions.map((tx) => (
                            <Card 
                                key={tx.id}
                                className={cn(
                                    "transaction-card cursor-pointer",
                                    tx.risk_level === 'high' && "border-l-4 border-l-rose-500"
                                )}
                                onClick={() => {
                                    setSelectedTx(tx);
                                    setDetailModalOpen(true);
                                }}
                                data-testid={`transaction-item-${tx.id}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "p-2 rounded-full",
                                                tx.type === 'sale' 
                                                    ? "bg-emerald-100 dark:bg-emerald-900/30"
                                                    : "bg-rose-100 dark:bg-rose-900/30"
                                            )}>
                                                {tx.type === 'sale' ? (
                                                    <ArrowUpRight className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                                ) : (
                                                    <ArrowDownRight className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-medium">{tx.category}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {formatDateTime(tx.created_at)}
                                                </p>
                                                {tx.description && (
                                                    <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                                                        {tx.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className={cn(
                                                "font-bold font-number text-lg",
                                                tx.type === 'sale' ? "text-emerald-600" : "text-rose-600"
                                            )}>
                                                {tx.type === 'sale' ? '+' : '-'}{formatCurrency(tx.amount)}
                                            </p>
                                            <div className="flex items-center gap-1 justify-end">
                                                {tx.risk_score && (
                                                    <Badge 
                                                        variant="outline" 
                                                        className={cn(
                                                            "text-xs",
                                                            tx.risk_level === 'high' && "border-rose-500 text-rose-500",
                                                            tx.risk_level === 'medium' && "border-amber-500 text-amber-500",
                                                            tx.risk_level === 'low' && "border-emerald-500 text-emerald-500"
                                                        )}
                                                    >
                                                        {tx.risk_score}
                                                    </Badge>
                                                )}
                                                <Badge 
                                                    variant="outline"
                                                    className={cn(
                                                        "text-xs",
                                                        tx.status === 'accepted' && "border-emerald-500 text-emerald-500",
                                                        tx.status === 'rejected' && "border-rose-500 text-rose-500",
                                                        tx.status === 'flagged' && "border-purple-500 text-purple-500",
                                                        tx.status === 'pending' && "border-amber-500 text-amber-500"
                                                    )}
                                                >
                                                    {tx.status}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 text-muted-foreground">
                        <p>{language === 'en' ? 'No transactions found' : 'कोई लेन-देन नहीं मिला'}</p>
                    </div>
                )}

                {/* Add Transaction Modal */}
                <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
                    <DialogContent data-testid="add-transaction-modal">
                        <DialogHeader>
                            <DialogTitle>
                                {newTx.type === 'sale' ? t('addSale') : t('addExpense')}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            {/* Type Toggle */}
                            <div className="flex gap-2">
                                <Button
                                    variant={newTx.type === 'sale' ? 'default' : 'outline'}
                                    className={cn(
                                        "flex-1",
                                        newTx.type === 'sale' && "bg-emerald-500 hover:bg-emerald-600"
                                    )}
                                    onClick={() => setNewTx(prev => ({ ...prev, type: 'sale' }))}
                                    data-testid="type-sale-btn"
                                >
                                    <ArrowUpRight className="w-4 h-4 mr-1" />
                                    {t('sale')}
                                </Button>
                                <Button
                                    variant={newTx.type === 'expense' ? 'default' : 'outline'}
                                    className={cn(
                                        "flex-1",
                                        newTx.type === 'expense' && "bg-rose-500 hover:bg-rose-600"
                                    )}
                                    onClick={() => setNewTx(prev => ({ ...prev, type: 'expense' }))}
                                    data-testid="type-expense-btn"
                                >
                                    <ArrowDownRight className="w-4 h-4 mr-1" />
                                    {t('expense')}
                                </Button>
                            </div>

                            {/* Amount */}
                            <div>
                                <label className="text-sm font-medium">{t('amount')}</label>
                                <div className="relative mt-1">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                                    <Input
                                        type="number"
                                        placeholder="0"
                                        value={newTx.amount}
                                        onChange={(e) => setNewTx(prev => ({ ...prev, amount: e.target.value }))}
                                        className="pl-8 text-2xl font-number h-14"
                                        data-testid="amount-input"
                                    />
                                </div>
                            </div>

                            {/* Category */}
                            <div>
                                <label className="text-sm font-medium">{t('category')}</label>
                                <Select
                                    value={newTx.category}
                                    onValueChange={(value) => setNewTx(prev => ({ ...prev, category: value }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CATEGORIES.map(cat => (
                                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="text-sm font-medium">{t('description')}</label>
                                <Input
                                    placeholder={language === 'en' ? 'Optional note...' : 'वैकल्पिक नोट...'}
                                    value={newTx.description}
                                    onChange={(e) => setNewTx(prev => ({ ...prev, description: e.target.value }))}
                                    className="mt-1"
                                    data-testid="description-input"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAddModalOpen(false)}>
                                {t('cancel')}
                            </Button>
                            <Button 
                                onClick={handleAddTransaction}
                                disabled={!newTx.amount || saving}
                                data-testid="save-transaction-btn"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                {t('save')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Transaction Detail Modal */}
                <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
                    <DialogContent className="max-w-md" data-testid="transaction-detail-modal">
                        {selectedTx && (
                            <>
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2">
                                        {selectedTx.type === 'sale' ? (
                                            <ArrowUpRight className="w-5 h-5 text-emerald-500" />
                                        ) : (
                                            <ArrowDownRight className="w-5 h-5 text-rose-500" />
                                        )}
                                        {selectedTx.category}
                                    </DialogTitle>
                                </DialogHeader>
                                
                                <div className="space-y-6 py-4">
                                    {/* Amount */}
                                    <div className="text-center">
                                        <p className={cn(
                                            "text-4xl font-bold font-number",
                                            selectedTx.type === 'sale' ? "text-emerald-600" : "text-rose-600"
                                        )}>
                                            {selectedTx.type === 'sale' ? '+' : '-'}{formatCurrency(selectedTx.amount)}
                                        </p>
                                        <p className="text-muted-foreground text-sm mt-1">
                                            {formatDateTime(selectedTx.created_at)}
                                        </p>
                                    </div>

                                    {/* Risk Gauge */}
                                    {selectedTx.risk_score && (
                                        <div className="flex flex-col items-center">
                                            <RiskGauge score={selectedTx.risk_score} size="md" />
                                            <p className="mt-4 text-sm text-center text-muted-foreground">
                                                {language === 'en' ? selectedTx.risk_reason_en : selectedTx.risk_reason_hi}
                                            </p>
                                        </div>
                                    )}

                                    {/* Details */}
                                    <div className="space-y-2 text-sm">
                                        {selectedTx.description && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">{t('description')}</span>
                                                <span>{selectedTx.description}</span>
                                            </div>
                                        )}
                                        {selectedTx.vpa && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">VPA</span>
                                                <span className="font-mono text-xs">{selectedTx.vpa}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Status</span>
                                            <Badge variant="outline">{selectedTx.status}</Badge>
                                        </div>
                                    </div>

                                    {/* Actions for high risk / pending */}
                                    {(selectedTx.risk_level === 'high' || selectedTx.status === 'pending') && (
                                        <div className="space-y-2 pt-4 border-t">
                                            <Button
                                                className="w-full bg-emerald-500 hover:bg-emerald-600"
                                                onClick={() => handleUpdateStatus('accepted')}
                                                data-testid="accept-btn"
                                            >
                                                <Check className="w-4 h-4 mr-2" />
                                                {t('acceptAnyway')}
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                className="w-full"
                                                onClick={() => handleUpdateStatus('rejected')}
                                                data-testid="reject-btn"
                                            >
                                                <X className="w-4 h-4 mr-2" />
                                                {t('rejectFlag')}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="w-full border-purple-500 text-purple-600 hover:bg-purple-50"
                                                onClick={() => {
                                                    handleUpdateStatus('flagged');
                                                    window.open('tel:1930', '_blank');
                                                }}
                                                data-testid="report-btn"
                                            >
                                                <Flag className="w-4 h-4 mr-2" />
                                                {t('reportFraud')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </MobileLayout>
    );
};

export default Transactions;
